/**
 * Fault Pipeline Types
 *
 * The Fault Pipeline manages device failures from intake through resolution.
 *
 * Pipeline Stages (4-stage model, Jul 2026 redesign):
 * 01-Triage → 02-Remote (Remote Support) → 03-Deploy → 04-Review
 *
 * Plus a parallel holding lane:
 * 07-Awaiting-Customer — a fault from ANY active stage can be parked here
 * while waiting on the customer. When the customer responds, the office moves
 * the fault back to whichever active stage makes sense next.
 *
 * Stage moves are MANUAL — moving a fault between stages never auto-fires
 * side effects. Deployment, tech consults, and billing are explicit action
 * buttons on the fault detail modal / sidebar.
 *
 * Exit actions from any post-triage stage:
 * - Send to Billing (fee-for-service callout)
 * - Send to Deal (requires new solution/product replacement)
 * - Send to Service Request (turned out to be a service change, not a fault)
 *
 * Historical note: the previous 6-stage model (01-Intake, 02-LVL1, 03-LVL2,
 * 04-LVL3, 05-Post-Deploy, 06-Follow-Up) plus two even older stages
 * (02-Tech-Consult, 03-Resolution) live forever in Firestore docs —
 * fault.stage, investigationHistory[].stage, attachments[].stage and
 * jobs.briefingHistory.timeline[].stage. ALWAYS read stage values through
 * migrateFaultStage().
 */

import type { Material, AdditionalGearItem, JobCompletionReasoning } from './schema';
import type { BillingLineItem, BillingTaxMode } from '@shared/money/billing-lines';

export type FaultStage =
  | '01-Triage'
  | '02-Remote'
  | '03-Deploy'
  | '04-Review'
  | '07-Awaiting-Customer';

/** Legacy stages for backward compatibility (pre-Jul-2026 models) */
export type LegacyFaultStage =
  | '01-Intake'
  | '02-LVL1'
  | '03-LVL2'
  | '04-LVL3'
  | '05-Post-Deploy'
  | '06-Follow-Up'
  | '02-Tech-Consult'
  | '03-Resolution';

/**
 * Map old stages to new stages for existing Firestore documents.
 * Covers all 9 historical literals (6-stage model + 2 pre-V107.52.14 values)
 * plus the current 5. Unknown values fall back to Triage.
 */
export function migrateFaultStage(stage: string): FaultStage {
  switch (stage) {
    // Current 4+1 model — pass through
    case '01-Triage': return '01-Triage';
    case '02-Remote': return '02-Remote';
    case '03-Deploy': return '03-Deploy';
    case '04-Review': return '04-Review';
    case '07-Awaiting-Customer': return '07-Awaiting-Customer';
    // 6-stage model (V107.52.14 → Jul 2026)
    case '01-Intake': return '01-Triage';
    case '02-LVL1': return '02-Remote';
    case '03-LVL2': return '02-Remote';
    case '04-LVL3': return '03-Deploy';
    case '05-Post-Deploy': return '04-Review';
    case '06-Follow-Up': return '04-Review';
    // Pre-V107.52.14 legacy
    case '02-Tech-Consult': return '02-Remote';
    case '03-Resolution': return '03-Deploy';
    default: return '01-Triage';
  }
}

/** Active stage order with the parking lane LAST — index arithmetic in
 * fault.service.ts and QuickStatusUpdate relies on both properties. */
export const FAULT_STAGE_ORDER: FaultStage[] = [
  '01-Triage',
  '02-Remote',
  '03-Deploy',
  '04-Review',
  '07-Awaiting-Customer',
];

export const FAULT_STAGE_LABELS: Record<FaultStage, string> = {
  '01-Triage': 'Triage',
  '02-Remote': 'Remote Support',
  '03-Deploy': 'Deploy',
  '04-Review': 'Review',
  '07-Awaiting-Customer': 'Awaiting Customer',
};

export const FAULT_STAGE_DESCRIPTIONS: Record<FaultStage, string> = {
  '01-Triage': 'New fault — capture details and work out what it needs',
  '02-Remote': 'Office working it remotely — diagnostics, reboots, customer guidance',
  '03-Deploy': 'On-site work — technician scheduled or in the field',
  '04-Review': 'Work done — review the outcome, bill, and close out',
  '07-Awaiting-Customer': 'Parked — waiting on the customer to respond, confirm, or take action',
};

export const FAULT_STAGE_COLORS: Record<FaultStage, {
  dot: string;
  bg: string;
  text: string;
  badge: string;
  bar: string;
  border: string;
}> = {
  '01-Triage': {
    dot: 'bg-sky-400',
    bg: 'from-sky-500/20 to-sky-600/5',
    text: 'text-sky-400',
    badge: 'bg-sky-500/20 text-sky-300',
    bar: 'bg-sky-400/60',
    border: 'border-sky-500/20',
  },
  '02-Remote': {
    dot: 'bg-blue-400',
    bg: 'from-blue-500/20 to-blue-600/5',
    text: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-300',
    bar: 'bg-blue-400/60',
    border: 'border-blue-500/20',
  },
  '03-Deploy': {
    dot: 'bg-purple-400',
    bg: 'from-purple-500/20 to-purple-600/5',
    text: 'text-purple-400',
    badge: 'bg-purple-500/20 text-purple-300',
    bar: 'bg-purple-400/60',
    border: 'border-purple-500/20',
  },
  '04-Review': {
    dot: 'bg-emerald-400',
    bg: 'from-emerald-500/20 to-emerald-600/5',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-300',
    bar: 'bg-emerald-400/60',
    border: 'border-emerald-500/20',
  },
  '07-Awaiting-Customer': {
    dot: 'bg-orange-400',
    bg: 'from-orange-500/20 to-orange-600/5',
    text: 'text-orange-400',
    badge: 'bg-orange-500/20 text-orange-300',
    bar: 'bg-orange-400/60',
    border: 'border-orange-500/20',
  },
};

export type ResolutionType =
  | 'deployed'
  | 'resolved_remotely'
  | 'closed_duplicate'
  | 'closed_no_fault_found';

export type IntakeMethod = 'Manual' | 'Auto' | 'live_call' | 'email';

export interface RemoteLog {
  id: string;
  timestamp: string; // ISO string
  author: string; // User ID or name
  action: 'diagnostic' | 'reboot' | 'notes' | 'configuration_change';
  notes: string;
}

export interface CallNote {
  id: string;
  timestamp: string; // ISO string
  author: string; // User ID or name
  notes: string;
  callDuration?: number; // Minutes
}

export interface ClientUpdate {
  id: string;
  timestamp: string; // ISO string
  author: string; // User ID or name
  method: 'phone' | 'sms' | 'email';
  content: string; // What was communicated to the client
  nextUpdateDue?: string; // ISO string — when we promised to update them next
}

export interface InvestigationHistoryEntry {
  id: string;
  timestamp: string; // ISO string
  stage: FaultStage | string; // string for backward compat with old stage values
  author: string; // User ID or name
  action: string;
  details: string;
}

/** Attachment kind - drives icon + rendering in the timeline */
export type FaultAttachmentKind = 'photo' | 'video' | 'file' | 'email';

/**
 * Attachment on a fault ticket — photos, files (PDFs, docs, logs), or logged emails.
 * Photos/files are uploaded to Firebase Storage; emails are stored inline (no storagePath).
 */
export interface FaultAttachment {
  id: string;
  kind: FaultAttachmentKind;
  timestamp: string; // ISO string — when attached
  addedBy: string; // User ID or display name
  addedByName?: string; // Denormalized display name

  // File / photo / video fields (kind === 'photo' | 'video' | 'file')
  fileName?: string;
  fileSize?: number; // Bytes
  mimeType?: string;
  url?: string; // Firebase Storage download URL
  storagePath?: string; // Path in Storage bucket
  thumbnailUrl?: string; // For photos (videos are stored without a generated thumbnail)

  // Email-specific fields (kind === 'email')
  emailDirection?: 'inbound' | 'outbound';
  emailSubject?: string;
  emailFrom?: string;
  emailTo?: string;
  emailBody?: string; // Full email text
  emailSummary?: string; // Short preview (first 200 chars)

  // Shared
  caption?: string; // User-provided note
  stage?: FaultStage; // Stage the attachment belongs to (defaults to fault.stage at time of add)
}

export interface Fault {
  id?: string;

  // Multi-tenant isolation (OPTIONAL — historical faults pre-dated this field; new writes must set it)
  tenantId?: string;

  /**
   * Human-readable reference ("FLT-0017") assigned once at creation — see
   * shared/src/domain/ticket-number.ts. Optional: faults created before the
   * backfill ran have none. Display/search only; the doc id stays the key.
   */
  ticketNumber?: string;

  // Customer & Device
  customerId: string;
  clientName: string;

  // Linked Customer Service (Phase 6: Faults Integration)
  serviceId?: string; // Link to customer_services/{serviceId}
  serviceName?: string; // Service display name
  serviceType?: string; // "NBN" | "Starlink" | "4G/5G" | "WiFi Extension" | etc.
  servicePlanName?: string; // Plan name (e.g., "NBN Fast 100")
  serviceMonthlyPrice?: number; // For context
  serviceActivatedAt?: string; // When service was activated

  deviceInfo?: {
    type: string; // "Router" | "Access Point" | "Switch" | "Camera" | etc.
    model?: string;
    serialNumber?: string;
    location?: string; // Within the property
  };

  // Address (for deployment handoff)
  address: string;
  lat?: number;
  lng?: number;

  // Stage Management
  stage: FaultStage;

  // 01-Intake
  intakeMethod: IntakeMethod;
  intakeNotes: string;
  intakeDate: string; // ISO timestamp
  intakeBy: string; // User ID or name
  callDuration?: number; // Seconds — recorded from live call timer

  // Fault Description
  symptom: string; // "No internet" | "Intermittent connection" | "Device offline" | etc.
  severity: 'low' | 'medium' | 'high' | 'critical';
  affectedServices?: string[]; // What's broken

  // Remote Investigation Logs (part of intake/triage)
  remoteLogs: RemoteLog[];

  // 03-LVL2 (Tech Consult)
  consultRequested: boolean;
  consultRequestedAt?: string; // ISO timestamp
  consultRequestedBy?: string;
  consultCompletedAt?: string;
  consultNotes?: string;
  assignedTechId?: string; // Tech who needs to call

  // Last tech consult response (for office view)
  lastConsultResponse?: {
    techId: string;
    techName: string;
    completedAt: string; // ISO timestamp
    recommendation: 'deploy' | 'continue_investigation' | 'resolved_remotely' | 'escalate';
    notes: string;
    estimatedDuration?: number; // minutes for deployment
  };

  // Call Notes (across all stages)
  callNotes: CallNote[];

  // Client Communication Log (outbound updates to the customer)
  clientUpdates: ClientUpdate[];

  // Investigation History (comprehensive timeline)
  investigationHistory: InvestigationHistoryEntry[];

  // Attachments — photos, files, and logged emails (V107.52.34)
  attachments?: FaultAttachment[];

  /**
   * Field briefing — the concise "do this, do that" the attending tech reads
   * first (Aug 2026). Same shape and same role as `deal.techBriefing`: ONE
   * human-authored instruction, captured at the moment the fault is sent to
   * the field, rather than leaving the tech to reconstruct the job from the
   * investigation trail. Written from FaultDeploymentModal / QuickRaiseJobModal
   * and rendered by the tech job card above the notes.
   */
  techBriefing?: import('./schema').TechBriefing;

  // Resolution
  resolutionType?: ResolutionType;
  resolvedAt?: string; // ISO timestamp
  resolvedBy?: string;
  resolutionNotes?: string;
  deploymentJobId?: string; // Link to created job in jobs collection (if deployed)

  // Provisioning (optional step before deployment)
  needsProvisioning?: boolean;
  provisioningStatus?: 'pending' | 'in_progress' | 'completed';
  provisioningAssignedTechIds?: string[];
  provisioningNotes?: string;
  provisioningSentAt?: string; // ISO timestamp
  provisioningSentBy?: string;
  provisioningCompletedAt?: string; // ISO timestamp
  provisioningCompletedBy?: string;

  // Quick-raise provisioning path tracking (audit trail)
  quickRaisedJobProvisioningPath?: 'provisioning' | 'scheduling' | null;

  // Exit Actions
  sentToBilling?: boolean;
  sentToBillingAt?: string;
  sentToBillingBy?: string;
  sentToDeals?: boolean;
  sentToDealsAt?: string;
  sentToDealsBy?: string;
  linkedDealId?: string; // Deal created from this fault (Send to Deal)

  // Cross-entity conversion links (Jul 2026 unified actions)
  sentToServiceRequest?: boolean;
  sentToServiceRequestAt?: string;
  sentToServiceRequestBy?: string;
  linkedServiceRequestId?: string; // Service request created from this fault (Send to Service Request)
  sourceServiceRequestId?: string; // Set when this fault was created FROM a service request

  // Ad-hoc jobs raised from this fault via Raise a Job (distinct from deploymentJobId)
  quickRaisedJobIds?: string[];

  // Billing lifecycle (after sentToBilling — tracked on /home/billing)
  // 'bad_debtor' is a manual office override for invoiced faults not getting paid.
  billingStatus?: 'to_bill' | 'billed' | 'paid' | 'bad_debtor';
  billedAt?: string;
  billedBy?: string;
  billedByName?: string;
  paidAt?: string;
  paidBy?: string;
  paidByName?: string;
  billingAmount?: number; // Ex-GST subtotal. When billingItems exist this is their sum.
  billingNotes?: string;
  // What's being billed — captured at "Send to Billing" time so the billing
  // section has a concrete description/line items instead of an empty $0 card.
  // Sell-side prices (ex GST). Distinct from costs.materials (internal cost
  // tracking). When present, billingAmount is the sum of these items.
  billingItems?: BillingLineItem[];
  // Tax treatment captured alongside the items. `billingAmount` stays ex-GST
  // in every mode — these fields say how it was entered and what tax was
  // actually charged, so /home/billing renders the captured figure instead of
  // re-grossing at today's tenant rate (which would restate a "no GST" job).
  billingTaxMode?: BillingTaxMode;
  billingTaxRate?: number; // Decimal rate SNAPSHOT at capture (0.1 = 10%)
  billingTaxAmount?: number;
  billingTotalIncTax?: number;
  badDebtorReason?: string;
  badDebtorFlaggedAt?: string;
  badDebtorFlaggedBy?: string;
  badDebtorFlaggedByName?: string;

  // Xero Integration (V107.52.37+) — populated on first successful push.
  // See docs/XERO_INTEGRATION_PLAN.md §4.3 and src/types/xero.types.ts.
  xeroInvoiceId?: string;       // Xero InvoiceID (UUID) — stable forever
  xeroInvoiceNumber?: string;   // e.g. "INV-2026-0042"
  xeroInvoiceUrl?: string;      // Deep link to view in Xero
  xeroInvoiceStatus?: 'DRAFT' | 'SUBMITTED' | 'AUTHORISED' | 'PAID' | 'VOIDED' | 'DELETED';
  xeroLastSyncedAt?: string;    // ISO timestamp of most recent push/status sync
  xeroPushError?: string;       // Last user-friendly error if push failed (cleared on success)

  // Follow-up Email
  followUpEmailDraft?: string; // AI-generated follow-up email
  followUpEmailSentAt?: string;
  followUpEmailSentBy?: string;

  // Lifecycle
  archived: boolean;
  archivedAt?: string;

  // Assignment
  assignedTo?: string; // Office staff member handling investigation

  // Timestamps
  createdAt: string; // ISO string
  updatedAt: string; // ISO string

  // Monitoring (post-deployment)
  monitoringPhase?: {
    active: boolean;
    startedAt: string; // ISO timestamp
    expiresAt: string; // 48 hours from job completion
    alertsSent: number;
  };

  // Follow-up (post-resolution)
  followUpDueDate?: string; // ISO timestamp - 4-5 days after resolution
  followUpCompleted?: boolean;
  followUpCompletedAt?: string; // ISO timestamp
  followUpNotes?: string;

  // Flodesk "Fault Follow Up" enrolment (set server-side by the fault
  // dispatcher when the fault completes — deployment job done OR resolved
  // remotely). The customer is enrolled immediately; the 7-day wait is a
  // delay node inside the Flodesk workflow. Once-per-fault via the timestamp.
  faultFollowUpFlodeskAt?: string;              // ISO — enrolment evaluated once
  faultFollowUpFlodeskStatus?: 'enrolled' | 'skipped'; // skipped = no email / workflow unset

  // SLA Tracking
  slaResponseDeadline?: string; // ISO timestamp — first action must happen before this
  slaResolutionDeadline?: string; // ISO timestamp — fault must be resolved/deployed before this
  slaResponseMet?: boolean; // True if first action was within response SLA
  slaResolutionMet?: boolean; // True if resolved within resolution SLA
  slaBreached?: boolean; // True if either SLA was missed

  // Repeat Fault Detection
  relatedFaults?: {
    faultId: string;
    symptom: string;
    createdAt: string; // ISO string
    resolutionType?: string;
  }[];
  repeatFlag?: boolean; // True if 2+ related faults found in 60 days
  repeatCount?: number; // Total including this one

  // Cost Tracking
  costs?: FaultCosts;

  // Deployment Job Completion (synced back from field when job finishes)
  deploymentJobCompleted?: boolean;       // True when the deployment job reaches COMPLETED
  deploymentJobCompletedAt?: string;      // ISO timestamp
  deploymentJobCompletedBy?: string;      // techId who completed
  deploymentJobSummary?: {
    techName?: string;                    // Tech who completed
    actualDuration?: number;              // Minutes on site
    completionNote?: string;              // Tech's wrap-up note
    fieldLogCount?: number;               // Number of field log entries
    fieldLogEntries?: {                   // Actual field log entries from the tech
      timestamp: string;
      text: string;
      techName?: string;
      entryType?: string;                 // observation/hypothesis/action/result/ruled_out/decision/note
    }[];
    photoCount?: number;                  // Number of photos taken
    needsReturnVisit?: boolean;           // Tech flagged return visit
    returnVisitReason?: string;           // Why return visit is needed
    customerSummary?: string;             // AI-generated customer-facing summary
    technicalReport?: string;             // AI-generated technical report
    completionReasoning?: JobCompletionReasoning; // Tech's JobCompletionReasoning synced from the job
    photos?: {                            // Photo refs synced from the completed job
      url: string;
      caption?: string;
      takenAt?: string;
      takenBy?: string;
    }[];
    additionalGearUsed?: AdditionalGearItem[]; // Extra gear used on site (what/qty/reason)
    usedMaterials?: Material[];           // Materials confirmed used on site
  };

  // Reopen history — populated when a completed fault is sent back to the field.
  // Each entry snapshots the previous deployment before clearing deployment state
  // so we keep an audit trail even after re-deployments overwrite the main fields.
  reopenHistory?: FaultReopenEntry[];
  reopenCount?: number;                   // Number of times this fault has been reopened
}

/**
 * Snapshot of a completed deployment at the moment a fault was reopened.
 * Lives in fault.reopenHistory[] — newest appended with arrayUnion.
 */
export interface FaultReopenEntry {
  reopenedAt: string;                     // ISO timestamp of the reopen
  reopenedBy: string;                     // userId who reopened
  reopenedByName?: string;                // Display name (denormalized)
  reason: string;                         // Why it was sent back to the field
  previousDeploymentJobId?: string;       // Job that had already completed
  previousDeploymentCompletedAt?: string;
  previousDeploymentSummary?: Fault['deploymentJobSummary'];
}

export interface FaultMaterialItem {
  description: string;
  quantity: number;
  unitCost: number;
  /** Optional provenance link when the line was added from the Parts Hub catalog */
  partsCatalogId?: string;
}

/**
 * A single line of "what's being billed" for a fault callout, captured when the
 * fault is sent to billing. Sell-side (what the customer is charged), ex GST.
 * Surfaced on the FaultBillingCard at /home/billing.
 *
 * @deprecated Use `BillingLineItem` from `@shared/money/billing-lines` — the
 * shared contract faults, service requests and the MCP connector all share.
 * This alias keeps existing importers compiling; the shape is a superset of
 * the original three fields, so stored docs read unchanged.
 */
export type FaultBillingItem = BillingLineItem;

export interface FaultCosts {
  labourMinutes: number; // Auto-accumulated from calls + on-site time
  labourCost: number; // labourMinutes / 60 × LABOUR_RATE_PER_HOUR
  materials: FaultMaterialItem[];
  materialsCost: number; // Sum of (quantity × unitCost)
  totalCost: number; // labourCost + materialsCost
}

/**
 * Briefing History - Formatted for tech view
 * This is what the tech sees when opening a job that originated from a fault
 */
export interface JobBriefingHistory {
  source: 'fault_pipeline';
  faultId: string;
  originalIntakeDate: string;

  // Consolidated timeline
  timeline: {
    timestamp: string;
    stage: string;
    author: string;
    type: 'call' | 'investigation' | 'diagnostic' | 'note' | 'client_update';
    content: string;
  }[];

  // Summary
  symptom: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  deviceInfo?: Fault['deviceInfo'];
  remoteDiagnosticsSummary?: string;
  serviceType?: string; // e.g. "NBN", "Starlink" — from fault
  servicePlanName?: string; // e.g. "NBN Fast 100" — from fault
}

/**
 * Fault Statistics (for dashboard)
 */
export interface FaultStats {
  totalOpen: number;
  byStage: Record<FaultStage, number>;
  bySeverity: {
    low: number;
    medium: number;
    high: number;
    critical: number;
  };
  averageResolutionTime: number; // Hours
  deploymentRate: number; // Percentage of faults that become jobs
  remoteResolutionRate: number; // Percentage resolved without deployment
  slaCompliance: number; // Percentage of resolved faults where SLA was met
}
